import React, { useState, useEffect } from "react";
import { Settings, UserPlus, Trash2, ChevronDown, ChevronUp, Save, Eye, EyeOff, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { getUserRole } from "./Login";

const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;

const DEFAULT_COMMISSIONS = {
  regie: {
    bleu: {
      petit: { r174: 1000, rMPR: 2000, r174Cum: 1000, rMPRCum: 2000 },
      moyen: { r174: 1500, rMPR: 2500, r174Cum: 1500, rMPRCum: 2500 },
      grand: { r174: 2000, rMPR: 3000, r174Cum: 2000, rMPRCum: 3000 },
    }
  }
};

const DEFAULT_COMMISSIONS_TELEPRO = {
  regie: {
    bleu: {
      petit: { r174: 500, rMPR: 500, r174Cum: 250, rMPRCum: 250 },
      moyen: { r174: 500, rMPR: 500, r174Cum: 250, rMPRCum: 250 },
      grand: { r174: 500, rMPR: 500, r174Cum: 250, rMPRCum: 250 },
    }
  }
};

const DEFAULT_DELAYS = {
  "174": "paye",
  mpr: "prime_mpr",
  cumule_174: "paye",
  cumule_mpr: "prime_mpr",
};

const STEPS_174_OPTIONS = [
  { value: "audit_avant_travaux", label: "Audit avant travaux" },
  { value: "devis_signe", label: "Devis signé" },
  { value: "planification_locataires", label: "Planification locataires" },
  { value: "date_demarrage_travaux", label: "Date démarrage travaux" },
  { value: "travaux_realises", label: "Travaux réalisés" },
  { value: "cofrac", label: "COFRAC" },
  { value: "signature_doc_clients", label: "Signature Doc clients" },
  { value: "depot", label: "Dépôt" },
  { value: "paye", label: "Payé" },
];

const STEPS_MPR_OPTIONS = [
  { value: "devis_signe", label: "Devis signé" },
  { value: "compte_mpr", label: "Compte MPR" },
  { value: "dossier_valide", label: "Dossier validé Ynergie" },
  { value: "dossier_octroye", label: "Dossier octroyé" },
  { value: "travaux_a_realiser", label: "Travaux à réaliser" },
  { value: "dossier_installe", label: "Travaux terminés" },
  { value: "prime_cee", label: "Prime CEE encaissée" },
  { value: "prime_apa", label: "APA encaissée" },
  { value: "prime_mpr", label: "MPR encaissée" },
  { value: "sav", label: "SAV" },
];

const SURFACE_LABELS = { petit: "< 70 m²", moyen: "70 - 130 m²", grand: "> 130 m²" };

function CommissionField({ label, value, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground w-20 shrink-0">{label}</span>
      <Input
        type="number" value={value} onChange={e => onChange(Number(e.target.value))}
        className="h-8 text-xs rounded-lg w-24"
        data-testid={`commission-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
      />
      <span className="text-xs text-muted-foreground">EUR</span>
    </div>
  );
}

function CommissionBlock({ title, data, onChange, colorClass }) {
  return (
    <div className={cn("border rounded-xl p-4 space-y-3", colorClass)}>
      <h4 className="text-sm font-bold">{title}</h4>
      <div className="grid grid-cols-2 gap-2">
        <CommissionField label="174" value={data.r174} onChange={v => onChange({ ...data, r174: v })} />
        <CommissionField label="MPR" value={data.rMPR} onChange={v => onChange({ ...data, rMPR: v })} />
        <CommissionField label="174 Cum." value={data.r174Cum} onChange={v => onChange({ ...data, r174Cum: v })} />
        <CommissionField label="MPR Cum." value={data.rMPRCum} onChange={v => onChange({ ...data, rMPRCum: v })} />
      </div>
    </div>
  );
}

function CreateRegieForm({ onCreated }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [commissions, setCommissions] = useState(JSON.parse(JSON.stringify(DEFAULT_COMMISSIONS)));
  const [delays, setDelays] = useState({ ...DEFAULT_DELAYS });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const updateComm = (path, value) => {
    setCommissions(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const handleCreate = async () => {
    if (!username.trim() || !password.trim()) {
      toast.error("Identifiant et mot de passe requis");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: username.trim(), password, role: "regie", commissions, payment_delays: delays }),
      });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.detail || "Erreur");
        setSaving(false);
        return;
      }
      toast.success(`Utilisateur "${username}" créé avec succès !`);
      setUsername("");
      setPassword("");
      setCommissions(JSON.parse(JSON.stringify(DEFAULT_COMMISSIONS)));
      setDelays({ ...DEFAULT_DELAYS });
      onCreated();
    } catch (err) {
      toast.error("Erreur de connexion");
    }
    setSaving(false);
  };

  return (
    <div className="border-2 border-dashed border-[hsl(145,100%,45%)]/40 rounded-2xl bg-card p-6 space-y-5" data-testid="create-regie-form">
      <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-2 w-full">
        <UserPlus className="w-5 h-5 text-[hsl(145,100%,39%)]" />
        <h3 className="font-heading font-bold text-lg flex-1 text-left">Créer un nouvel accès Régie</h3>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {expanded && (
        <div className="space-y-6">
          {/* Identifiant & Mot de passe */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Identifiant *</Label>
              <Input value={username} onChange={e => setUsername(e.target.value)} placeholder="Ex: Régie Thomas" className="rounded-xl" data-testid="input-regie-username" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Mot de passe *</Label>
              <div className="relative">
                <Input type={showPwd ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="Mot de passe" className="rounded-xl pr-10" data-testid="input-regie-password" />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          {/* Commissions */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm border-b border-border pb-2">Commission régies</h4>

            {/* Regie - Bleu par surface */}
            <div className="border border-blue-500/30 bg-blue-500/10/30 rounded-xl p-4 space-y-3">
              <h4 className="text-sm font-bold text-blue-700">Par surface</h4>
              {["petit", "moyen", "grand"].map(size => (
                <div key={size} className="space-y-2">
                  <p className="text-xs font-semibold text-blue-600">{SURFACE_LABELS[size]}</p>
                  <div className="grid grid-cols-2 gap-2 pl-3">
                    <CommissionField label="174" value={commissions.regie.bleu[size].r174} onChange={v => updateComm(`regie.bleu.${size}.r174`, v)} />
                    <CommissionField label="MPR" value={commissions.regie.bleu[size].rMPR} onChange={v => updateComm(`regie.bleu.${size}.rMPR`, v)} />
                    <CommissionField label="174 Cum." value={commissions.regie.bleu[size].r174Cum} onChange={v => updateComm(`regie.bleu.${size}.r174Cum`, v)} />
                    <CommissionField label="MPR Cum." value={commissions.regie.bleu[size].rMPRCum} onChange={v => updateComm(`regie.bleu.${size}.rMPRCum`, v)} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Délais de paiement */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm border-b border-border pb-2">Délais de paiement commission</h4>
            <p className="text-xs text-muted-foreground">A quelle étape du dossier la commission régie est-elle versée ?</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Programme 174</Label>
                <Select value={delays["174"]} onValueChange={v => setDelays(p => ({ ...p, "174": v }))}>
                  <SelectTrigger className="rounded-xl" data-testid="delay-174"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STEPS_174_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Programme MPR</Label>
                <Select value={delays.mpr} onValueChange={v => setDelays(p => ({ ...p, mpr: v }))}>
                  <SelectTrigger className="rounded-xl" data-testid="delay-mpr"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STEPS_MPR_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Op. Cumulée - Partie 174</Label>
                <Select value={delays.cumule_174} onValueChange={v => setDelays(p => ({ ...p, cumule_174: v }))}>
                  <SelectTrigger className="rounded-xl" data-testid="delay-cumule-174"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STEPS_174_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Op. Cumulée - Partie MPR</Label>
                <Select value={delays.cumule_mpr} onValueChange={v => setDelays(p => ({ ...p, cumule_mpr: v }))}>
                  <SelectTrigger className="rounded-xl" data-testid="delay-cumule-mpr"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STEPS_MPR_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Commission SCI */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Commission SCI (% HT du devis)</Label>
            <div className="flex items-center gap-2">
              <Input type="number" min="0" max="100" step="0.5" value={commissions.commission_sci || 10} onChange={e => setCommissions(prev => ({ ...prev, commission_sci: parseFloat(e.target.value) || 10 }))} className="w-24 rounded-xl" data-testid="input-comm-sci" />
              <span className="text-xs text-muted-foreground">% HT du montant devis (TVA 5,5%)</span>
            </div>
          </div>

          {/* Submit */}
          <Button onClick={handleCreate} disabled={saving || !username.trim() || !password.trim()}
            className="rounded-xl bg-[hsl(145,100%,45%)] hover:bg-[hsl(145,100%,39%)] text-white shadow-lg"
            data-testid="btn-create-regie"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {saving ? "Création..." : "Créer l'accès Régie"}
          </Button>
        </div>
      )}
    </div>
  );
}

function RegieUserCard({ user, onDeleted }) {
  const [expanded, setExpanded] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [changingPwd, setChangingPwd] = useState(false);
  const currentUser = localStorage.getItem("label_energie_user") || "";
  const currentRole = getUserRole();

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await fetch(`${API}/users/${encodeURIComponent(user.username)}`, { method: "DELETE" });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.detail || "Erreur");
      } else {
        toast.success(`"${user.username}" supprimé`);
        onDeleted();
      }
    } catch (err) { toast.error("Erreur"); }
    setDeleting(false);
    setDeleteConfirm(false);
  };

  const handleChangePassword = async () => {
    if (!newPassword.trim() || newPassword.length < 4) { toast.error("Le mot de passe doit contenir au moins 4 caractères"); return; }
    setChangingPwd(true);
    try {
      const res = await fetch(`${API}/users/${encodeURIComponent(user.username)}/change-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ requester: currentUser, new_password: newPassword }),
      });
      if (!res.ok) { const err = await res.json(); toast.error(err.detail || "Erreur"); }
      else { toast.success(`Mot de passe de "${user.username}" modifié`); setNewPassword(""); }
    } catch (err) { toast.error("Erreur"); }
    setChangingPwd(false);
  };

  // Determine if current user can change this user's password
  const canChangePassword = (() => {
    // Renova can change everyone
    if (currentRole === "admin_full") return true;
    // Admin (admin_restricted) and administratif can change regie and telepro
    if (currentRole === "admin_restricted" || currentRole === "administratif") {
      return user.role === "regie" || user.role === "telepro" || user.role === "administratif";
    }
    return false;
  })();

  const comm = user.commissions || {};
  const delays = user.payment_delays || {};
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "-";
  const [editingComm, setEditingComm] = useState(false);
  const [editComm, setEditComm] = useState(JSON.parse(JSON.stringify(comm)));
  const [savingComm, setSavingComm] = useState(false);

  const handleSaveCommissions = async () => {
    setSavingComm(true);
    try {
      const res = await fetch(`${API}/users/${encodeURIComponent(user.username)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ commissions: editComm }),
      });
      if (res.ok) { toast.success("Commissions mises à jour"); setEditingComm(false); onDeleted(); }
      else toast.error("Erreur");
    } catch (err) { toast.error("Erreur"); }
    setSavingComm(false);
  };

  const updateCommField = (path, val) => {
    setEditComm(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) { if (!obj[keys[i]]) obj[keys[i]] = {}; obj = obj[keys[i]]; }
      obj[keys[keys.length - 1]] = parseFloat(val) || 0;
      return next;
    });
  };

  return (
    <div className="border border-border rounded-xl bg-card overflow-hidden" data-testid={`regie-user-${user.username}`}>
      <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
            <span className="text-sm font-bold text-orange-600">{user.username.charAt(0).toUpperCase()}</span>
          </div>
          <div>
            <p className="font-semibold text-sm">{user.username}</p>
            <p className="text-xs text-muted-foreground">Créé le {fmtDate(user.created_at)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); setDeleteConfirm(true); }}
            className="text-red-500 hover:text-red-700 hover:bg-red-500/10 h-8 w-8 p-0 rounded-lg" data-testid={`btn-delete-${user.username}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
          {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
          {/* Commission summary — editable */}
          {(comm.regie || editingComm) && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Commissions</p>
                {!editingComm ? (
                  <button onClick={() => { setEditComm(JSON.parse(JSON.stringify(comm))); setEditingComm(true); }} className="text-[10px] text-blue-600 font-bold hover:underline">Modifier</button>
                ) : (
                  <div className="flex gap-2">
                    <button onClick={() => setEditingComm(false)} className="text-[10px] text-muted-foreground">Annuler</button>
                    <button onClick={handleSaveCommissions} disabled={savingComm} className="text-[10px] text-emerald-600 font-bold">{savingComm ? "..." : "Enregistrer"}</button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                {(editingComm ? editComm : comm).regie?.bleu && Object.entries((editingComm ? editComm : comm).regie.bleu).map(([size, vals]) => (
                  <div key={size} className="border border-blue-500/30 rounded-lg p-2 bg-blue-500/10">
                    <p className="font-bold text-blue-700 dark:text-blue-400 text-[10px] uppercase mb-1">Bleu - {SURFACE_LABELS[size]}</p>
                    {editingComm ? (
                      <div className="grid grid-cols-2 gap-1">
                        <div><label className="text-[9px]">174</label><Input type="number" className="h-6 text-xs rounded-sm" value={vals.r174} onChange={e => updateCommField(`regie.bleu.${size}.r174`, e.target.value)} /></div>
                        <div><label className="text-[9px]">MPR</label><Input type="number" className="h-6 text-xs rounded-sm" value={vals.rMPR} onChange={e => updateCommField(`regie.bleu.${size}.rMPR`, e.target.value)} /></div>
                        <div><label className="text-[9px]">174 Cum</label><Input type="number" className="h-6 text-xs rounded-sm" value={vals.r174Cum} onChange={e => updateCommField(`regie.bleu.${size}.r174Cum`, e.target.value)} /></div>
                        <div><label className="text-[9px]">MPR Cum</label><Input type="number" className="h-6 text-xs rounded-sm" value={vals.rMPRCum} onChange={e => updateCommField(`regie.bleu.${size}.rMPRCum`, e.target.value)} /></div>
                      </div>
                    ) : (
                      <div className="space-y-0.5">
                        <p>174: <strong>{vals.r174} EUR</strong> | MPR: <strong>{vals.rMPR} EUR</strong></p>
                        <p>174 Cum: <strong>{vals.r174Cum} EUR</strong> | MPR Cum: <strong>{vals.rMPRCum} EUR</strong></p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {/* Commission SCI */}
              <div className="border border-violet-500/30 rounded-lg p-2 bg-violet-500/10">
                <p className="font-bold text-violet-700 dark:text-violet-400 text-[10px] uppercase mb-1">Commission SCI</p>
                {editingComm ? (
                  <div className="flex items-center gap-2">
                    <Input type="number" className="h-6 w-20 text-xs rounded-sm" value={editComm.commission_sci ?? 10} onChange={e => setEditComm(prev => ({ ...prev, commission_sci: parseFloat(e.target.value) || 10 }))} />
                    <span className="text-[10px] text-muted-foreground">% HT du devis</span>
                  </div>
                ) : (
                  <p className="text-xs"><strong>{comm.commission_sci ?? 10}%</strong> HT du montant devis</p>
                )}
              </div>
            </div>
          )}

          {/* Payment delays */}
          {delays && Object.keys(delays).length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Délais de paiement</p>
              <div className="flex flex-wrap gap-2 text-xs">
                {Object.entries(delays).map(([k, v]) => {
                  const allSteps = [...STEPS_174_OPTIONS, ...STEPS_MPR_OPTIONS];
                  const stepLabel = allSteps.find(s => s.value === v)?.label || v;
                  const progLabel = k === "174" ? "174" : k === "mpr" ? "MPR" : k === "cumule_174" ? "Cum. 174" : "Cum. MPR";
                  return (
                    <span key={k} className="px-2 py-1 rounded-lg bg-muted border border-border">
                      <strong>{progLabel}</strong>: {stepLabel}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Password change */}
          {canChangePassword && (
            <div className="space-y-2 border-t border-border pt-3">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Modifier le mot de passe</p>
              <div className="flex items-center gap-2">
                <div className="relative flex-1 max-w-[250px]">
                  <Input type={showNewPwd ? "text" : "password"} value={newPassword} onChange={e => setNewPassword(e.target.value)}
                    placeholder="Nouveau mot de passe" className="rounded-lg h-8 text-sm pr-8" data-testid={`pwd-input-${user.username}`} />
                  <button type="button" onClick={() => setShowNewPwd(!showNewPwd)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showNewPwd ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
                <Button size="sm" onClick={handleChangePassword} disabled={changingPwd || !newPassword.trim()}
                  className="rounded-lg h-8 text-xs" data-testid={`btn-change-pwd-${user.username}`}>
                  <Save className="w-3.5 h-3.5 mr-1" />
                  {changingPwd ? "..." : "Modifier"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <AlertDialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer "{user.username}" ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action désactivera le compte. L'utilisateur ne pourra plus se connecter.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting}
              className="rounded-xl bg-destructive text-destructive-foreground">
              {deleting ? "Suppression..." : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CreateTeleproForm({ onCreated }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [commissions, setCommissions] = useState(JSON.parse(JSON.stringify(DEFAULT_COMMISSIONS_TELEPRO)));
  const [delays, setDelays] = useState({ ...DEFAULT_DELAYS });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const updateComm = (path, value) => {
    setCommissions(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const handleCreate = async () => {
    if (!username.trim() || !password.trim()) { toast.error("Identifiant et mot de passe requis"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: username.trim(), password, role: "telepro", commissions, payment_delays: delays }),
      });
      if (!res.ok) { const err = await res.json(); toast.error(err.detail || "Erreur"); setSaving(false); return; }
      toast.success(`Télépro "${username}" créé avec succès !`);
      setUsername(""); setPassword("");
      setCommissions(JSON.parse(JSON.stringify(DEFAULT_COMMISSIONS_TELEPRO)));
      setDelays({ ...DEFAULT_DELAYS });
      onCreated();
    } catch (err) { toast.error("Erreur de connexion"); }
    setSaving(false);
  };

  return (
    <div className="border-2 border-dashed border-blue-400/40 rounded-2xl bg-card p-6 space-y-5" data-testid="create-telepro-form">
      <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-2 w-full">
        <UserPlus className="w-5 h-5 text-blue-500" />
        <h3 className="font-heading font-bold text-lg flex-1 text-left">Créer un nouvel accès Télépro</h3>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {expanded && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Identifiant *</Label>
              <Input value={username} onChange={e => setUsername(e.target.value)} placeholder="Ex: Télépro Thomas" className="rounded-xl" data-testid="input-telepro-username" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Mot de passe *</Label>
              <div className="relative">
                <Input type={showPwd ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="Mot de passe" className="rounded-xl pr-10" data-testid="input-telepro-password" />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          {/* Commissions (simpler - flat per program) */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm border-b border-border pb-2">Commissions Télépro</h4>
            <div className="border border-blue-500/30 bg-blue-500/10/30 rounded-xl p-4 space-y-3">
              <p className="text-xs text-muted-foreground">Par défaut : 500 EUR pour 174/MPR, 250 EUR + 250 EUR pour Op. Cumulée</p>
              {["petit", "moyen", "grand"].map(size => (
                <div key={size} className="space-y-2">
                  <p className="text-xs font-semibold text-blue-600">{SURFACE_LABELS[size]}</p>
                  <div className="grid grid-cols-2 gap-2 pl-3">
                    <CommissionField label="174" value={commissions.regie.bleu[size].r174} onChange={v => updateComm(`regie.bleu.${size}.r174`, v)} />
                    <CommissionField label="MPR" value={commissions.regie.bleu[size].rMPR} onChange={v => updateComm(`regie.bleu.${size}.rMPR`, v)} />
                    <CommissionField label="174 Cum." value={commissions.regie.bleu[size].r174Cum} onChange={v => updateComm(`regie.bleu.${size}.r174Cum`, v)} />
                    <CommissionField label="MPR Cum." value={commissions.regie.bleu[size].rMPRCum} onChange={v => updateComm(`regie.bleu.${size}.rMPRCum`, v)} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Délais de paiement */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm border-b border-border pb-2">Délais de paiement commission</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Programme 174</Label>
                <Select value={delays["174"]} onValueChange={v => setDelays(p => ({ ...p, "174": v }))}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{STEPS_174_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Programme MPR</Label>
                <Select value={delays.mpr} onValueChange={v => setDelays(p => ({ ...p, mpr: v }))}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{STEPS_MPR_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Op. Cumulée - 174</Label>
                <Select value={delays.cumule_174} onValueChange={v => setDelays(p => ({ ...p, cumule_174: v }))}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{STEPS_174_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Op. Cumulée - MPR</Label>
                <Select value={delays.cumule_mpr} onValueChange={v => setDelays(p => ({ ...p, cumule_mpr: v }))}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{STEPS_MPR_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Commission SCI */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Commission SCI (% HT du devis)</Label>
            <div className="flex items-center gap-2">
              <Input type="number" min="0" max="100" step="0.5" value={commissions.commission_sci || 10} onChange={e => setCommissions(prev => ({ ...prev, commission_sci: parseFloat(e.target.value) || 10 }))} className="w-24 rounded-xl" data-testid="input-comm-sci-telepro" />
              <span className="text-xs text-muted-foreground">% HT du montant devis (TVA 5,5%)</span>
            </div>
          </div>

          <Button onClick={handleCreate} disabled={saving || !username.trim() || !password.trim()}
            className="rounded-xl bg-blue-500 hover:bg-blue-600 text-white shadow-lg" data-testid="btn-create-telepro">
            <UserPlus className="w-4 h-4 mr-2" />
            {saving ? "Création..." : "Créer l'accès Télépro"}
          </Button>
        </div>
      )}
    </div>
  );
}

export default function SettingsPage() {
  const [regieUsers, setRegieUsers] = useState([]);
  const [teleproUsers, setTeleproUsers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentRole = getUserRole();
  const isRenova = currentRole === "admin_full";

  const fetchUsers = () => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/users-regie`).then(r => r.json()),
      fetch(`${API}/users-telepro`).then(r => r.json()),
      fetch(`${API}/users-administratif`).then(r => r.json()),
    ]).then(([regies, telepros, admins]) => {
      setRegieUsers(regies);
      setTeleproUsers(telepros);
      setAdminUsers(admins);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchUsers(); }, []);

  // Password change for Renova and Admin
  const [pwdRenova, setPwdRenova] = useState("");
  const [pwdAdmin, setPwdAdmin] = useState("");
  const [showPwdR, setShowPwdR] = useState(false);
  const [showPwdA, setShowPwdA] = useState(false);
  const [savingPwdR, setSavingPwdR] = useState(false);
  const [savingPwdA, setSavingPwdA] = useState(false);

  const handleChangeSelfPassword = async (username, pwd, setSaving, setPwd) => {
    if (!pwd.trim() || pwd.length < 4) { toast.error("Min. 4 caractères"); return; }
    setSaving(true);
    try {
      const requester = localStorage.getItem("label_energie_user") || "";
      const res = await fetch(`${API}/users/${encodeURIComponent(username)}/change-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ requester, new_password: pwd }),
      });
      if (!res.ok) { const err = await res.json(); toast.error(err.detail || "Erreur"); }
      else { toast.success(`Mot de passe de "${username}" modifié`); setPwd(""); }
    } catch (err) { toast.error("Erreur"); }
    setSaving(false);
  };

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-5xl animate-fade-in" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-foreground flex items-center gap-3">
          <Settings className="w-8 h-8 text-[hsl(187,71%,59%)]" />
          Paramètres
        </h1>
        <p className="text-muted-foreground mt-1">Gestion des accès Régie, Télépro, Administratif et des commissions</p>
      </div>

      {/* Self-password section for Renova */}
      {isRenova && (
        <div className="border border-border rounded-xl bg-card p-5 space-y-4">
          <h3 className="font-heading font-bold text-base">Modifier les mots de passe Renova / Admin</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Mot de passe Renova</Label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Input type={showPwdR ? "text" : "password"} value={pwdRenova} onChange={e => setPwdRenova(e.target.value)} placeholder="Nouveau mot de passe" className="rounded-lg pr-8" data-testid="pwd-renova" />
                  <button type="button" onClick={() => setShowPwdR(!showPwdR)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"><Eye className="w-3.5 h-3.5" /></button>
                </div>
                <Button size="sm" onClick={() => handleChangeSelfPassword("Renova", pwdRenova, setSavingPwdR, setPwdRenova)} disabled={savingPwdR || !pwdRenova.trim()} className="rounded-lg" data-testid="btn-pwd-renova">
                  <Save className="w-3.5 h-3.5 mr-1" />{savingPwdR ? "..." : "OK"}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Mot de passe Admin</Label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Input type={showPwdA ? "text" : "password"} value={pwdAdmin} onChange={e => setPwdAdmin(e.target.value)} placeholder="Nouveau mot de passe" className="rounded-lg pr-8" data-testid="pwd-admin" />
                  <button type="button" onClick={() => setShowPwdA(!showPwdA)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"><Eye className="w-3.5 h-3.5" /></button>
                </div>
                <Button size="sm" onClick={() => handleChangeSelfPassword("Admin", pwdAdmin, setSavingPwdA, setPwdAdmin)} disabled={savingPwdA || !pwdAdmin.trim()} className="rounded-lg" data-testid="btn-pwd-admin">
                  <Save className="w-3.5 h-3.5 mr-1" />{savingPwdA ? "..." : "OK"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recalculate Dashboard — Renova only */}
      {isRenova && (
        <div className="border border-border rounded-xl bg-card p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-heading font-bold text-base">Mise à jour des données financières</h3>
              <p className="text-xs text-muted-foreground mt-1">Recalcule toutes les données du dashboard (CA, marges, encaissements) en scannant chaque dossier. Auto-refresh toutes les 5 min.</p>
            </div>
            <Button
              onClick={async () => {
                try {
                  toast.info("Recalcul en cours...");
                  const res = await fetch(`${API}/recalculate-dashboard`, { method: "POST" });
                  const data = await res.json();
                  toast.success(`${data.updated_dossiers} dossiers mis à jour`);
                } catch (err) { toast.error("Erreur lors du recalcul"); }
              }}
              className="rounded-xl bg-[hsl(var(--renova-green))] hover:bg-[hsl(var(--renova-green-hover))] text-white"
              data-testid="btn-recalculate"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Recalculer maintenant
            </Button>
          </div>
        </div>
      )}

      <Tabs defaultValue="all-users" className="w-full">
        <TabsList className="bg-muted/50 p-1 rounded-xl mb-6">
          <TabsTrigger value="all-users" className="rounded-xl px-6 py-2.5 font-semibold data-[state=active]:bg-emerald-500/10 data-[state=active]:text-emerald-700" data-testid="tab-settings-all-users">
            Tous les utilisateurs
          </TabsTrigger>
          <TabsTrigger value="regie" className="rounded-xl px-6 py-2.5 font-semibold data-[state=active]:bg-orange-500/10 data-[state=active]:text-orange-700" data-testid="tab-settings-regie">
            Régie ({regieUsers.length})
          </TabsTrigger>
          <TabsTrigger value="telepro" className="rounded-xl px-6 py-2.5 font-semibold data-[state=active]:bg-blue-500/10 data-[state=active]:text-blue-700" data-testid="tab-settings-telepro">
            Télépro ({teleproUsers.length})
          </TabsTrigger>
          {isRenova && (
            <TabsTrigger value="administratif" className="rounded-xl px-6 py-2.5 font-semibold data-[state=active]:bg-violet-500/10 data-[state=active]:text-violet-700" data-testid="tab-settings-administratif">
              Administratif ({adminUsers.length})
            </TabsTrigger>
          )}
        </TabsList>

        {/* All Users Tab */}
        <TabsContent value="all-users" className="space-y-6">
          <AllUsersTab onUpdated={fetchUsers} />
        </TabsContent>

        <TabsContent value="regie" className="space-y-6">
          <CreateRegieForm onCreated={fetchUsers} />
          <div className="space-y-3">
            <h3 className="font-heading font-bold text-lg">Accès Régie existants ({regieUsers.length})</h3>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Chargement...</div>
            ) : regieUsers.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-border rounded-xl">
                <p className="text-muted-foreground">Aucun accès Régie configuré</p>
              </div>
            ) : (
              <div className="space-y-3">
                {regieUsers.map(u => <RegieUserCard key={u.id} user={u} onDeleted={fetchUsers} />)}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="telepro" className="space-y-6">
          <CreateTeleproForm onCreated={fetchUsers} />
          <div className="space-y-3">
            <h3 className="font-heading font-bold text-lg">Accès Télépro existants ({teleproUsers.length})</h3>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Chargement...</div>
            ) : teleproUsers.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-border rounded-xl">
                <p className="text-muted-foreground">Aucun accès Télépro dynamique configuré</p>
              </div>
            ) : (
              <div className="space-y-3">
                {teleproUsers.map(u => <RegieUserCard key={u.id} user={u} onDeleted={fetchUsers} />)}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Administratif Tab */}
        {isRenova && (
          <TabsContent value="administratif" className="space-y-6">
            <CreateAdministratifForm regieUsers={regieUsers} teleproUsers={teleproUsers} onCreated={fetchUsers} />
            <div className="space-y-3">
              <h3 className="font-heading font-bold text-lg">Utilisateurs Administratif existants ({adminUsers.length})</h3>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Chargement...</div>
              ) : adminUsers.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-border rounded-xl">
                  <p className="text-muted-foreground">Aucun utilisateur Administratif configuré</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {adminUsers.map(u => <AdminUserCard key={u.id} user={u} regieUsers={regieUsers} teleproUsers={teleproUsers} onUpdated={fetchUsers} />)}
                </div>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}


function AllUsersTab({ onUpdated }) {
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null); // { username, newUsername, newPassword }
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editAssign, setEditAssign] = useState(null); // username being assigned
  const [editAssignRegies, setEditAssignRegies] = useState([]);
  const [savingAssign, setSavingAssign] = useState(false);
  const currentUser = localStorage.getItem("label_energie_user") || "";

  const ROLE_LABELS = { admin_full: "Admin Complet", admin_restricted: "Admin Restreint", regie: "Régie", telepro: "Télépro", administratif: "Administratif" };
  const ROLE_COLORS = { admin_full: "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400", admin_restricted: "bg-amber-500/20 text-amber-600 dark:text-amber-400", regie: "bg-orange-500/20 text-orange-600 dark:text-orange-400", telepro: "bg-blue-500/20 text-blue-600 dark:text-blue-400", administratif: "bg-violet-500/20 text-violet-600 dark:text-violet-400" };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/users`);
      const data = await res.json();
      setAllUsers(data);
    } catch (err) { setAllUsers([]); }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const handleSaveAssign = async (username) => {
    setSavingAssign(true);
    try {
      await fetch(`${API}/users/${encodeURIComponent(username)}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assigned_regies: editAssignRegies }),
      });
      toast.success("Attribution mise à jour");
      setEditAssign(null);
      fetchAll();
    } catch (err) { toast.error("Erreur"); }
    setSavingAssign(false);
  };

  const handleSave = async (user) => {
    if (!editUser) return;
    setSaving(true);
    try {
      // Change username if modified
      if (editUser.newUsername && editUser.newUsername !== user.username) {
        const res = await fetch(`${API}/users/${encodeURIComponent(user.username)}/rename`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ new_username: editUser.newUsername }),
        });
        if (!res.ok) { const err = await res.json(); toast.error(err.detail || "Erreur renommage"); setSaving(false); return; }
      }
      // Change password if provided
      if (editUser.newPassword && editUser.newPassword.trim()) {
        const targetUsername = editUser.newUsername || user.username;
        const res = await fetch(`${API}/users/${encodeURIComponent(targetUsername)}/change-password`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ requester: currentUser, new_password: editUser.newPassword }),
        });
        if (!res.ok) { const err = await res.json(); toast.error(err.detail || "Erreur mot de passe"); setSaving(false); return; }
      }
      toast.success(`Utilisateur "${editUser.newUsername || user.username}" mis à jour`);
      setEditUser(null);
      fetchAll();
      onUpdated();
    } catch (err) { toast.error("Erreur"); }
    setSaving(false);
  };

  const handleDelete = async (username) => {
    setDeleting(true);
    try {
      const res = await fetch(`${API}/users/${encodeURIComponent(username)}`, { method: "DELETE" });
      if (!res.ok) { const err = await res.json(); toast.error(err.detail || "Erreur"); }
      else { toast.success(`"${username}" supprimé`); fetchAll(); onUpdated(); }
    } catch (err) { toast.error("Erreur"); }
    setDeleting(false); setDeleteConfirm(null);
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground">Chargement...</div>;

  return (
    <div className="space-y-4" data-testid="all-users-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-bold text-lg">Tous les utilisateurs actifs ({allUsers.length})</h3>
      </div>

      <div className="card-floating overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-border bg-muted/30">
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Utilisateur</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Rôle</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Nouveau mot de passe</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {allUsers.map(u => {
              const isEditing = editUser?.original === u.username;
              const isSelf = u.username === currentUser;
              const isRenovaAccount = u.role === "admin_full";
              const currentUserRole = localStorage.getItem("label_energie_role") || "";
              const isCurrentRenova = currentUserRole === "admin_full";
              // Admin restricted cannot see/edit Renova
              if (isRenovaAccount && !isCurrentRenova) return null;
              // Only Renova can edit Renova
              const canEdit = isRenovaAccount ? isCurrentRenova : true;
              // Renova account is never deletable
              const canDelete = !isSelf && !isRenovaAccount;
              return (
                <React.Fragment key={u.username}>
                <tr className={cn("border-b border-border/40 transition-colors", isEditing && "bg-emerald-500/10")} data-testid={`user-row-${u.username}`}>
                  <td className="px-4 py-3">
                    {isEditing ? (
                      <Input value={editUser.newUsername} onChange={e => setEditUser(p => ({ ...p, newUsername: e.target.value }))}
                        className="h-8 text-sm rounded-lg w-48" data-testid={`edit-username-${u.username}`} />
                    ) : (
                      <span className="font-semibold text-sm">{u.username}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn("px-2.5 py-1 rounded-full text-[10px] font-bold uppercase", ROLE_COLORS[u.role] || "bg-muted/50 text-gray-600")}>
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {isEditing ? (
                      <Input type="text" value={editUser.newPassword} onChange={e => setEditUser(p => ({ ...p, newPassword: e.target.value }))}
                        placeholder="Laisser vide pour ne pas changer" className="h-8 text-sm rounded-lg w-52" data-testid={`edit-pwd-${u.username}`} />
                    ) : (
                      <span className="text-xs text-muted-foreground">••••••••</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      {isEditing ? (
                        <>
                          <Button size="sm" onClick={() => handleSave(u)} disabled={saving}
                            className="rounded-lg h-7 text-xs bg-emerald-500 hover:bg-emerald-600" data-testid={`save-user-${u.username}`}>
                            <Save className="w-3.5 h-3.5 mr-1" />{saving ? "..." : "Sauver"}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditUser(null)} className="rounded-lg h-7 text-xs">
                            Annuler
                          </Button>
                        </>
                      ) : (
                        <>
                          {canEdit && (
                            <Button size="sm" variant="outline" onClick={() => setEditUser({ original: u.username, newUsername: u.username, newPassword: "" })}
                              className="rounded-lg h-7 text-xs" data-testid={`edit-user-${u.username}`}>
                              Modifier
                            </Button>
                          )}
                          {(u.role === "admin_restricted" || u.role === "administratif") && (
                            <Button size="sm" variant="outline" onClick={() => { setEditAssign(u.username); setEditAssignRegies(u.assigned_regies || []); }}
                              className="rounded-lg h-7 text-xs text-violet-600 border-violet-300" data-testid={`assign-user-${u.username}`}>
                              Attribuer
                            </Button>
                          )}
                          {canDelete && (
                            <Button size="sm" variant="ghost" onClick={() => setDeleteConfirm(u.username)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-500/10 h-7 w-7 p-0 rounded-lg" data-testid={`delete-user-${u.username}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {editAssign === u.username && (
                  <tr><td colSpan="4" className="px-4 py-3 bg-violet-500/5 border-b border-border">
                    <div className="space-y-2">
                      <p className="text-xs font-bold text-violet-600">Régies / Télépros attribués à {u.username}</p>
                      <div className="flex flex-wrap gap-2">
                        {allUsers.filter(au => au.role === "regie" || au.role === "telepro").map(r => (
                          <label key={r.username} className={cn("flex items-center gap-1.5 px-2 py-1 rounded-lg border cursor-pointer text-xs",
                            editAssignRegies.includes(r.username) ? "border-violet-400 bg-violet-500/20 font-bold" : "border-border hover:bg-muted/50"
                          )}>
                            <input type="checkbox" checked={editAssignRegies.includes(r.username)}
                              onChange={() => setEditAssignRegies(prev => prev.includes(r.username) ? prev.filter(x => x !== r.username) : [...prev, r.username])}
                              className="rounded border-border w-3 h-3" />
                            {r.username}
                            <span className={cn("text-[8px] px-1 rounded font-bold", r.role === "regie" ? "bg-orange-500/20 text-orange-600" : "bg-blue-500/20 text-blue-600")}>{r.role === "regie" ? "R" : "T"}</span>
                          </label>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleSaveAssign(u.username)} disabled={savingAssign} className="rounded-lg h-7 text-xs bg-violet-500 hover:bg-violet-600">
                          <Save className="w-3 h-3 mr-1" />{savingAssign ? "..." : "Sauvegarder"}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditAssign(null)} className="rounded-lg h-7 text-xs">Annuler</Button>
                      </div>
                    </div>
                  </td></tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer "{deleteConfirm}" ?</AlertDialogTitle>
            <AlertDialogDescription>L'utilisateur ne pourra plus se connecter.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteConfirm)} disabled={deleting}
              className="rounded-xl bg-destructive text-destructive-foreground">
              {deleting ? "Suppression..." : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function CreateAdministratifForm({ regieUsers, teleproUsers, onCreated }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [selectedRegies, setSelectedRegies] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const toggleRegie = (regieUsername) => {
    setSelectedRegies(prev =>
      prev.includes(regieUsername) ? prev.filter(r => r !== regieUsername) : [...prev, regieUsername]
    );
  };

  const handleCreate = async () => {
    if (!username.trim() || !password.trim()) { toast.error("Identifiant et mot de passe requis"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/users`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: username.trim(), password, role: "administratif", assigned_regies: selectedRegies }),
      });
      if (!res.ok) { const err = await res.json(); toast.error(err.detail || "Erreur"); setSaving(false); return; }
      toast.success(`Utilisateur Administratif "${username}" créé !`);
      setUsername(""); setPassword(""); setSelectedRegies([]);
      onCreated();
    } catch (err) { toast.error("Erreur de connexion"); }
    setSaving(false);
  };

  return (
    <div className="border-2 border-dashed border-violet-400/40 rounded-2xl bg-card p-6 space-y-5" data-testid="create-administratif-form">
      <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-2 w-full">
        <UserPlus className="w-5 h-5 text-violet-500" />
        <h3 className="font-heading font-bold text-lg flex-1 text-left">Créer un accès Administratif</h3>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {expanded && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Identifiant *</Label>
              <Input value={username} onChange={e => setUsername(e.target.value)} placeholder="Ex: Administratif Jean" className="rounded-xl" data-testid="input-admin-username" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Mot de passe *</Label>
              <div className="relative">
                <Input type={showPwd ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="Mot de passe" className="rounded-xl pr-10" data-testid="input-admin-password" />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          {/* Assign Régies + Télépros */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold">Régies / Télépros attribués</Label>
            <p className="text-xs text-muted-foreground">Sélectionnez les régies et télépros dont les dossiers seront visibles par cet utilisateur</p>
            <div className="border border-border rounded-xl p-3 max-h-[200px] overflow-y-auto space-y-1">
              {[...(regieUsers || []), ...(teleproUsers || [])].length === 0 ? (
                <p className="text-xs text-muted-foreground italic">Aucune régie ou télépro disponible</p>
              ) : [...(regieUsers || []), ...(teleproUsers || [])].map(r => (
                <label key={r.username} className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-muted/50 cursor-pointer" data-testid={`admin-assign-${r.username}`}>
                  <input type="checkbox" checked={selectedRegies.includes(r.username)} onChange={() => toggleRegie(r.username)}
                    className="rounded border-border" />
                  <span className="text-sm">{r.username}</span>
                  <span className={cn("text-[9px] px-1.5 py-0.5 rounded font-bold", r.role === "regie" ? "bg-orange-500/20 text-orange-600" : "bg-blue-500/20 text-blue-600")}>{r.role === "regie" ? "Régie" : "Télépro"}</span>
                </label>
              ))}
            </div>
            {selectedRegies.length > 0 && (
              <p className="text-xs text-violet-600 font-medium">{selectedRegies.length} utilisateur(s) sélectionné(s)</p>
            )}
          </div>

          <Button onClick={handleCreate} disabled={saving || !username.trim() || !password.trim()}
            className="rounded-xl bg-violet-500 hover:bg-violet-600 text-white shadow-lg" data-testid="btn-create-administratif">
            <UserPlus className="w-4 h-4 mr-2" />
            {saving ? "Création..." : "Créer l'accès Administratif"}
          </Button>
        </div>
      )}
    </div>
  );
}

function AdminUserCard({ user, regieUsers, teleproUsers, onUpdated }) {
  const [expanded, setExpanded] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editRegies, setEditRegies] = useState(user.assigned_regies || []);
  const [savingRegies, setSavingRegies] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [changingPwd, setChangingPwd] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await fetch(`${API}/users/${encodeURIComponent(user.username)}`, { method: "DELETE" });
      if (!res.ok) { const err = await res.json(); toast.error(err.detail || "Erreur"); }
      else { toast.success(`"${user.username}" supprimé`); onUpdated(); }
    } catch (err) { toast.error("Erreur"); }
    setDeleting(false); setDeleteConfirm(false);
  };

  const toggleRegie = (r) => {
    setEditRegies(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
  };

  const handleSaveRegies = async () => {
    setSavingRegies(true);
    try {
      const res = await fetch(`${API}/users/${encodeURIComponent(user.username)}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assigned_regies: editRegies }),
      });
      if (!res.ok) { const err = await res.json(); toast.error(err.detail || "Erreur"); }
      else { toast.success("Régies mises à jour"); onUpdated(); }
    } catch (err) { toast.error("Erreur"); }
    setSavingRegies(false);
  };

  const handleChangePassword = async () => {
    if (!newPassword.trim() || newPassword.length < 4) { toast.error("Min. 4 caractères"); return; }
    setChangingPwd(true);
    try {
      const requester = localStorage.getItem("label_energie_user") || "";
      const res = await fetch(`${API}/users/${encodeURIComponent(user.username)}/change-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ requester, new_password: newPassword }),
      });
      if (!res.ok) { const err = await res.json(); toast.error(err.detail || "Erreur"); }
      else { toast.success(`Mot de passe modifié`); setNewPassword(""); }
    } catch (err) { toast.error("Erreur"); }
    setChangingPwd(false);
  };

  return (
    <div className="border border-violet-500/30 rounded-xl bg-card overflow-hidden" data-testid={`admin-user-${user.username}`}>
      <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
            <span className="text-sm font-bold text-violet-600">{user.username.charAt(0).toUpperCase()}</span>
          </div>
          <div>
            <p className="font-semibold text-sm">{user.username}</p>
            <p className="text-xs text-muted-foreground">{(user.assigned_regies || []).length} régie(s) / télépro(s) attribué(s)</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); setDeleteConfirm(true); }}
            className="text-red-500 hover:text-red-700 hover:bg-red-500/10 h-8 w-8 p-0 rounded-lg">
            <Trash2 className="w-4 h-4" />
          </Button>
          {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-border pt-3">
          {/* Assigned régies */}
          <div className="space-y-2">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Régies / Télépros attribués</p>
            <div className="border border-border rounded-lg p-3 max-h-[180px] overflow-y-auto space-y-1">
              {[...(regieUsers || []), ...(teleproUsers || [])].map(r => (
                <label key={r.username} className="flex items-center gap-2 p-1 rounded-lg hover:bg-muted/50 cursor-pointer">
                  <input type="checkbox" checked={editRegies.includes(r.username)} onChange={() => toggleRegie(r.username)} className="rounded border-border" />
                  <span className="text-sm">{r.username}</span>
                  <span className={cn("text-[9px] px-1.5 py-0.5 rounded font-bold", r.role === "regie" ? "bg-orange-500/20 text-orange-600" : "bg-blue-500/20 text-blue-600")}>{r.role === "regie" ? "Régie" : "Télépro"}</span>
                </label>
              ))}
            </div>
            <Button size="sm" onClick={handleSaveRegies} disabled={savingRegies} className="rounded-lg h-7 text-xs bg-violet-500 hover:bg-violet-600" data-testid={`btn-save-regies-${user.username}`}>
              <Save className="w-3.5 h-3.5 mr-1" />{savingRegies ? "..." : "Sauvegarder"}
            </Button>
          </div>

          {/* Password change */}
          <div className="space-y-2 border-t border-border pt-3">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Modifier le mot de passe</p>
            <div className="flex items-center gap-2">
              <div className="relative flex-1 max-w-[250px]">
                <Input type={showNewPwd ? "text" : "password"} value={newPassword} onChange={e => setNewPassword(e.target.value)}
                  placeholder="Nouveau mot de passe" className="rounded-lg h-8 text-sm pr-8" />
                <button type="button" onClick={() => setShowNewPwd(!showNewPwd)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {showNewPwd ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>
              <Button size="sm" onClick={handleChangePassword} disabled={changingPwd || !newPassword.trim()} className="rounded-lg h-8 text-xs">
                <Save className="w-3.5 h-3.5 mr-1" />{changingPwd ? "..." : "Modifier"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer "{user.username}" ?</AlertDialogTitle>
            <AlertDialogDescription>Cette action désactivera le compte administratif.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="rounded-xl bg-destructive text-destructive-foreground">
              {deleting ? "Suppression..." : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
